import React from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiActivity, FiCheckCircle, FiClock, FiMessageSquare } = FiIcons;

const RecentActivity = ({ tasks }) => {
  const activities = tasks
    .flatMap(task => [
      {
        id: `task-${task.id}`,
        type: 'task_created',
        taskTitle: task.title,
        timestamp: task.createdAt,
        user: task.assignedBy
      },
      ...task.comments.map(comment => ({
        id: `comment-${comment.id}`,
        type: 'comment_added',
        taskTitle: task.title,
        timestamp: comment.createdAt,
        user: comment.userId
      })),
      ...(task.status === 'completed' ? [{
        id: `completed-${task.id}`,
        type: 'task_completed',
        taskTitle: task.title,
        timestamp: task.createdAt,
        user: task.assignedTo
      }] : [])
    ])
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 8);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'task_created':
        return FiClock;
      case 'task_completed':
        return FiCheckCircle;
      case 'comment_added':
        return FiMessageSquare;
      default:
        return FiActivity;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'task_created':
        return 'text-blue-600 bg-blue-50';
      case 'task_completed':
        return 'text-green-600 bg-green-50';
      case 'comment_added':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'task_created':
        return `New task "${activity.taskTitle}" was created`;
      case 'task_completed':
        return `Task "${activity.taskTitle}" was completed`;
      case 'comment_added':
        return `Comment added to "${activity.taskTitle}"`;
      default:
        return 'Activity occurred';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        <SafeIcon icon={FiActivity} className="w-5 h-5 text-gray-500" />
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities.map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-start space-x-3"
          >
            <div className={`p-2 rounded-full ${getActivityColor(activity.type)}`}>
              <SafeIcon 
                icon={getActivityIcon(activity.type)} 
                className="w-4 h-4"
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900">
                {getActivityText(activity)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
              </p>
            </div>
          </motion.div>
        ))}

        {activities.length === 0 && (
          <div className="text-center py-8">
            <SafeIcon icon={FiActivity} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No recent activity</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RecentActivity;